import React from "react";

import { defineMessages, useIntl } from "react-intl";

import { EntityType, EntityTypesSupportedByMapsPage } from "@mapmycustomers/shared/types/entity";
import { Alert } from "@mapmycustomers/ui";

import ButtonLink from "@app/component/ButtonLink";
import useMapView from "@app/component/view/utils/useMapView";

const message = defineMessages<EntityTypesSupportedByMapsPage>({
  [EntityType.COMPANY]: {
    id: "map.filters.entityNotVisibleAlert.company",
    defaultMessage: "Companies are not currently visible on your map",
    description:
      "Message of an alert shown when user adds filter for currently hidden companies on Maps page",
  },
  [EntityType.DEAL]: {
    id: "map.filters.entityNotVisibleAlert.deal",
    defaultMessage: "Deals are not currently visible on your map",
    description:
      "Message of an alert shown when user adds filter for currently hidden deals on Maps page",
  },
  [EntityType.PERSON]: {
    id: "map.filters.entityNotVisibleAlert.person",
    defaultMessage: "People are not currently visible on your map",
    description:
      "Message of an alert shown when user adds filter for currently hidden people on Maps page",
  },
});

const actionMessage = defineMessages<EntityTypesSupportedByMapsPage>({
  [EntityType.COMPANY]: {
    id: "map.filters.entityNotVisibleAlert.action.company",
    defaultMessage: "Show Companies",
    description:
      "Action label in alert show when user adds filter for currently hidden companies on Maps page",
  },
  [EntityType.DEAL]: {
    id: "map.filters.entityNotVisibleAlert.action.deal",
    defaultMessage: "Show Deals",
    description:
      "Action label in alert show when user adds filter for currently hidden deals on Maps page",
  },
  [EntityType.PERSON]: {
    id: "map.filters.entityNotVisibleAlert.action.person",
    defaultMessage: "Show People",
    description:
      "Action label in alert show when user adds filter for currently hidden people on Maps page",
  },
});

interface Props {
  entityType: EntityTypesSupportedByMapsPage;
}

const EntityNotVisible: React.FC<Props> = ({ entityType }) => {
  const intl = useIntl();

  const {
    updateViewStateAndFetch,
    viewState: { visibleEntities },
  } = useMapView();

  const handleClick = () => {
    const updatedVisibleEntities = visibleEntities.includes(entityType)
      ? visibleEntities // this case should not really happen, but let's not add duplicates
      : [...visibleEntities, entityType];
    updateViewStateAndFetch({ request: { visibleEntities: updatedVisibleEntities } });
  };

  return (
    <Alert
      action={
        <ButtonLink onClick={handleClick}>
          {intl.formatMessage(actionMessage[entityType])}
        </ButtonLink>
      }
      message={intl.formatMessage(message[entityType])}
      showIcon
      type="warning"
    />
  );
};

export default EntityNotVisible;
